import React, { useState } from 'react';
import { BookOpen, Plus, X, Check } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { PHASES, PHASE_COLOR } from './PhaseTimeline';

const PHASE_LABELS = Object.fromEntries(PHASES.map(p => [p.key, p.label]));

const JournalPanel = ({ opportunity, token, onUpdate }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opportunities/${opportunity._id}/journal`,
        { text, phase: opportunity.phase },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(data);
      setText('');
    } catch (e) {
      console.error('Error adding journal entry', e);
    } finally {
      setSaving(false);
    }
  };

  const entries = [...(opportunity.journal || [])].reverse(); // newest first

  return (
    <div>
      {/* New entry */}
      <div style={{ marginBottom: '20px' }}>
        <textarea
          placeholder="Escribe una nota sobre esta oportunidad..."
          value={text}
          onChange={e => setText(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            color: '#F0F0F0',
            padding: '10px',
            fontSize: '0.85rem',
            resize: 'vertical',
            minHeight: '80px',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: '8px',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleAdd}
            disabled={saving || !text.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: text.trim() ? '#E27B58' : 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: '6px',
              color: text.trim() ? 'white' : '#444',
              padding: '8px 16px',
              cursor: text.trim() ? 'pointer' : 'default',
              fontSize: '0.82rem',
              fontWeight: '700',
              transition: 'all 0.2s',
            }}
          >
            <Plus size={13} /> Añadir nota
          </button>
        </div>
      </div>

      {/* Entries timeline */}
      {entries.length === 0 && (
        <p style={{ color: '#444', fontSize: '0.8rem', fontStyle: 'italic' }}>No hay entradas en el diario todavía.</p>
      )}

      <div style={{ position: 'relative' }}>
        {entries.map((entry, i) => {
          const color = PHASE_COLOR[entry.phase] || '#555';
          return (
            <div key={entry._id || i} style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {/* Timeline dot */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: color,
                  border: `2px solid ${color}`,
                  marginTop: '4px',
                  boxShadow: `0 0 8px ${color}66`,
                }} />
                {i < entries.length - 1 && (
                  <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.06)', minHeight: 20 }} />
                )}
              </div>
              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                  {entry.phase && (
                    <span style={{
                      fontSize: '0.6rem',
                      color,
                      background: `${color}15`,
                      border: `1px solid ${color}33`,
                      padding: '1px 6px',
                      borderRadius: '3px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                    }}>
                      {PHASE_LABELS[entry.phase] || entry.phase}
                    </span>
                  )}
                  <span style={{ fontSize: '0.65rem', color: '#444', fontFamily: 'monospace' }}>
                    {new Date(entry.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  color: '#CCC',
                  lineHeight: '1.5',
                  background: 'rgba(255,255,255,0.02)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.04)',
                }}>
                  {entry.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JournalPanel;
